import { getAllTimezones } from "@/utils/timezones";
import type { TranslationF } from "@/utils/type-utils";
import { z } from "zod";

const SENDER_PRIORITY_ENUM = {
	EXCLUDED: "EXCLUDED",
	LOW: "LOW",
	MEDIUM: "MEDIUM",
	HIGH: "HIGH",
	VERY_HIGH: "VERY_HIGH",
} as const;

const SUPPORTED_TIMEZONES = /* @__PURE__ */ getAllTimezones();
const supportedTimezoneSet = /* @__PURE__ */ new Set(SUPPORTED_TIMEZONES);

export const getSenderGroupSchema = (t: TranslationF) =>
	z
		.object({
			name: z.string().min(1, t("validation.required")),
			timezone: z
				.string()
				.min(1, t("validation.required"))
				.refine(
					(tz) =>
						SUPPORTED_TIMEZONES.length === 0 || supportedTimezoneSet.has(tz),
					t("validation.invalidTimezone"),
				),
			to_keep: z
				.preprocess(
					(v) => (v === "" || v === null ? null : Number(v)),
					z.number().nullable(),
				)
				.optional(),
			use_global_setting: z.boolean().default(true),
			use_global_priority: z.boolean().default(true),
			priority_usage: z.nativeEnum(SENDER_PRIORITY_ENUM).nullable().optional(),
		})
		.superRefine((data, ctx) => {
			if (!data.use_global_setting && data.to_keep == null) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["to_keep"],
					message: t("validation.required"),
				});
			}
			if (!data.use_global_priority && data.priority_usage == null) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["priority_usage"],
					message: t("validation.required"),
				});
			}
		});

export type SenderGroupFormData = z.infer<
	ReturnType<typeof getSenderGroupSchema>
>;

export type SenderGroupInitialValues = {
	name: string;
	timezone: string;
	to_keep: number | null;
	use_global_setting: boolean;
	use_global_priority: boolean;
	priority_usage: "EXCLUDED" | "LOW" | "MEDIUM" | "HIGH" | "VERY_HIGH" | null;
};
